import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/types/database.types";

// Define the contact type used by the Contacts page
export interface Contact {
  id: string; // agent_chat_user_id
  name: string;
  email: string;
  conversation_id: string;
  conversation_date: string;
  agent_id: string;
  agent_name: string; // Display name
  agent_title: string; // Internal name
  state: "open" | "completed";
  // Analytics fields
  priority?: "low" | "medium" | "high" | "urgent" | null;
  intent?: string | null;
  next_step?: string | null;
  sentiment?: "positive" | "neutral" | "negative" | "unknown" | null;
  tags?: string[] | null;
  user_type?: "lead" | "client" | "unknown" | null;
  user_phone?: string | null;
  status?: "open" | "action_required" | "in_progress" | "closed" | "archived" | null;
  // Extraction status fields (using correct column names)
  extracted_fields_status?: boolean | null;
  extracted_information_status?: boolean | null;
  // Message count and time saved - now fetched from conversations table
  message_count?: number;
  estimated_time_saved?: number | null;
}

/**
 * Service for handling operations related to contacts derived from conversations
 * Primary data source: conversations table (not agent_chat_users)
 * Note: Each conversation appears as a separate contact because analytics data (sentiment, priority, etc.) is conversation-specific
 */
export const contactService = {
  /**
   * Fetches contacts from conversations (primary source) joined with agent_chat_users for user details
   * Each conversation becomes a separate contact entry since analytics data is conversation-specific
   * Now includes message_count and estimated_time_saved from conversations table
   */
  async getContacts(): Promise<{
    data: Contact[] | null;
    error: Error | null;
  }> {
    try {
      // Get current session / user
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        return { data: null, error: new Error("No authenticated user found") };
      }

      const userId = session.user.id;

      // 1. Retrieve organization_id for the user
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("id", userId)
        .single();

      if (profileError || !profileData?.organization_id) {
        throw profileError || new Error("Could not find organization for user");
      }

      const organizationId = profileData.organization_id as string;

      // 2. Retrieve agents that belong to the organization
      const { data: agentsData, error: agentsError } = await supabase
        .from("agents")
        .select("id, agent_name, title") // Fetch title as well
        .eq("organization_id", organizationId);

      if (agentsError) throw agentsError;
      if (!agentsData || agentsData.length === 0) {
        return { data: [], error: null };
      }

      const agentIds = agentsData.map((a) => a.id);
      // Update agentMap to store both name and title
      const agentMap: Record<string, { name: string; title: string }> = agentsData.reduce(
        (acc, agent) => {
          acc[agent.id] = {
            name: agent.agent_name ?? "Unknown Agent",
            title: agent.title ?? "Unknown Agent Title"
          };
          return acc;
        },
        {} as Record<string, { name: string; title: string }>
      );

      // 3. Fetch conversations (PRIMARY DATA SOURCE) with analytics fields AND estimated_time_saved joined with agent_chat_users for user details
      const { data: convData, error: convError } = await supabase
        .from("conversations")
        .select(
          `id, created_at, state, agent_id, agent_chat_user_id,
           priority, intent, next_step, sentiment, tags, user_type, status,
           extracted_fields_status, extracted_information_status,
           estimated_time_saved,
           agent_chat_users ( id, name, email, phone )`
        )
        .in("agent_id", agentIds)
        .order("created_at", { ascending: false });

      if (convError) throw convError;

      if (!convData) return { data: [], error: null };

      // Filter conversations to only include those with valid email users and organization-isolated agent_chat_users
      const validConversations = convData.filter(conv => {
        const chatUser = conv.agent_chat_users as Tables<"agent_chat_users"> | null;
        return chatUser && chatUser.email && chatUser.email !== "No email provided";
      });

      // 4. Calculate message counts for all conversations in batches (similar to inbox service)
      const messageCountMap: Record<string, number> = {};
      const conversationIds = validConversations.map((conv) => conv.id);

      // Process in batches to avoid query limits
      const BATCH_SIZE = 50;
      for (let i = 0; i < conversationIds.length; i += BATCH_SIZE) {
        const batchIds = conversationIds.slice(i, i + BATCH_SIZE);
        const { data: messageData } = await supabase
          .from("messages")
          .select("conversation_id, id")
          .in("conversation_id", batchIds);

        if (messageData) {
          // Group by conversation_id and count
          messageData.forEach((msg) => {
            if (!messageCountMap[msg.conversation_id]) {
              messageCountMap[msg.conversation_id] = 0;
            }
            messageCountMap[msg.conversation_id]++;
          });
        }
      }

      // Transform to Contact[] using conversation data as primary source
      // Each conversation becomes a separate contact entry (since analytics data is conversation-specific)
      const contacts: Contact[] = validConversations.map((conv) => {
        const chatUser = conv.agent_chat_users as Tables<"agent_chat_users">; // Already filtered, so we know it exists
        const agentInfo = agentMap[conv.agent_id] ?? { name: "Unknown Agent", title: "Unknown Agent Title" };
        const messageCount = messageCountMap[conv.id] || 0;
        
        return {
          id: chatUser.id,
          name: chatUser.name ?? "Anonymous User",
          email: chatUser.email as string, // Already filtered, so we know it's a string
          conversation_id: conv.id,
          conversation_date: conv.created_at,
          agent_id: conv.agent_id,
          agent_name: agentInfo.name,
          agent_title: agentInfo.title, // Use internal title
          state: conv.state as "open" | "completed",
          // Analytics fields from conversation (PRIMARY SOURCE) - these are conversation-specific
          priority: conv.priority as "low" | "medium" | "high" | "urgent" | null,
          intent: conv.intent,
          next_step: conv.next_step,
          sentiment: conv.sentiment as "positive" | "neutral" | "negative" | "unknown" | null,
          tags: conv.tags,
          user_type: conv.user_type as "lead" | "client" | "unknown" | null,
          user_phone: chatUser.phone,
          status: conv.status as "open" | "action_required" | "in_progress" | "closed" | "archived" | null,
          // Extraction status fields from conversation (PRIMARY SOURCE)
          extracted_fields_status: conv.extracted_fields_status === "success" ? true : conv.extracted_fields_status === "fail" ? false : null,
          extracted_information_status: conv.extracted_information_status === "success" ? true : conv.extracted_information_status === "fail" ? false : null,
          // Message count and time saved - now properly fetched
          message_count: messageCount,
          estimated_time_saved: conv.estimated_time_saved,
        };
      });

      return { data: contacts, error: null };
    } catch (error) {
      console.error("Error in getContacts:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Filters contacts by search term (name or email)
   */
  filterContacts(contacts: Contact[], term: string): Contact[] {
    if (!term) return contacts;
    const lower = term.toLowerCase();
    return contacts.filter(
      (c) => c.name.toLowerCase().includes(lower) || c.email.toLowerCase().includes(lower)
    );
  },
}; 